import { useState, useMemo, useCallback, createRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  PageLayout,
  SafetyCasePopup,
  SafetyCaseHazardPopup,
  Button,
  safetyCaseValuesToSafetyCaseAddUpsert,
  showSuccessAlert,
  showErrorAlert,
  ISafetyCaseValues,
} from '../../../components'
import ContentMapLayout from '../../../hoc/content-map-layout'
import Map from '../../../components/map/map'
import MarkerLayout from '../../../components/map/marker-layout'
import PolygonLayout from '../../../components/map/polygon-layout'
import SafetyCasesCardsList from './safety-cases-cards-list'
import SafetyCaseFilter from './safety-cases-filter'
import CreateSafetyCaseModal from './create-safety-case-modal'

import { useCreateSafetyCaseMutation, useSafetyCasesQuery } from '../../../services'
import { getGeoJson } from '../../../util'
import { queryParamsToParams, filterSafetyCase } from './safety-cases-page-utils'
import { useModal, useSafeQueryParams } from '../../../hooks'
import { claims, useAccessControl, useProfile } from '../../../security'
import { useAppContext } from '../../../app-context'

import { IGeoJson, ISafetyCase, ISafetyCaseHazard, IRefs, EntityState } from '../../../interfaces'

const pageTitle = 'Open Safety Cases'

const SafetyCasesPage = () => {
  const location = useLocation()
  const { airportId } = useProfile()
  const { dispatch: appDispatch } = useAppContext()

  const { hasAccess } = useAccessControl()
  const { isOpen: isCreateModalOpen, openModal: openCreateModal, closeModal: closeCreateModal } = useModal()

  const hasSafetyCaseWriteAccess = useMemo(() => hasAccess([claims.safetyCase.write]), [hasAccess])

  const { params, setParams } = useSafeQueryParams(queryParamsToParams)

  const navigate = useNavigate()

  const searchFilter = useMemo(() => params.searchFilter, [params.searchFilter])

  const setSearchFilter = useCallback(
    (values: { [key: string]: any }) => {
      setParams({ searchFilter: { ...searchFilter, ...values } })
    },
    [setParams, searchFilter],
  )

  const pagination = useMemo(() => params.pagination, [params.pagination])

  const setPagination = useCallback(
    (values: { [key: string]: number }) => {
      setParams({ pagination: { ...pagination, ...values } })
    },
    [setParams, pagination],
  )

  const [activeGeoJsonId, setActiveGeoJsonId] = useState<number | undefined>(undefined)
  const [infoGeoJsonId, setInfoGeoJsonId] = useState<number | undefined>(undefined)

  // There can be multiple hazards for a single safety case, so we need to be able to set multiple active hazard ids
  const [hazardActiveGeoJsonId, setHazardActiveGeoJsonId] = useState<number | number[] | undefined>(undefined)
  const [hazardInfoGeoJsonId, setHazardInfoGeoJsonId] = useState<number | undefined>(undefined)

  const {
    data: safetyCases,
    isInitialLoading: isSafetyCasesInitialLoading,
    isFetching: isSafetyCasesLoading,
  } = useSafetyCasesQuery({ entityState: EntityState.Open })

  const { mutateAsync: createSafetyCase, isLoading: isSafetyCaseCreating } = useCreateSafetyCaseMutation({})

  const filteredSafetyCases = useMemo(() => filterSafetyCase(safetyCases, searchFilter), [safetyCases, searchFilter])

  const refs = useMemo(
    () =>
      (safetyCases || []).reduce<IRefs>((acc, value) => {
        if (value.id) {
          acc[value.id] = createRef()
        }

        return acc
      }, {}),
    [safetyCases],
  )

  const { markersGeoJson: safetyCasesMarkersGeoJson, polygonsGeoJson: safetyCasesPolygonsGeoJson } = getGeoJson(
    filteredSafetyCases,
    (safetyCase: ISafetyCase) =>
      safetyCase.geoJson
        ? {
            ...safetyCase.geoJson,
            properties: { ...(safetyCase?.geoJson?.properties || {}), id: safetyCase.id },
          }
        : undefined,
  )

  const allHazards = useMemo(
    () => filteredSafetyCases.flatMap(safetyCase => safetyCase.hazards || []),
    [filteredSafetyCases],
  )

  const { markersGeoJson: hazardsMarkersGeoJson, polygonsGeoJson: hazardsPolygonsGeoJson } = getGeoJson(
    allHazards,
    (hazard: ISafetyCaseHazard) =>
      hazard.geoJson && {
        ...hazard.geoJson,
        properties: { ...(hazard.geoJson.properties || {}), id: hazard.id },
      },
  )

  const scrollToSafetyCaseCard = useCallback(
    (safetyCaseId: number) => {
      const safetyCasePageIndex = Math.floor(
        filteredSafetyCases.findIndex(safetyCase => safetyCase.id === safetyCaseId) / pagination.pageSize,
      )

      if (safetyCasePageIndex === pagination.pageIndex) {
        if (refs[safetyCaseId]) {
          refs[safetyCaseId].current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }
      } else {
        setPagination({
          pageIndex: safetyCasePageIndex,
        })

        setTimeout(() => {
          if (refs[safetyCaseId]) {
            refs[safetyCaseId].current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }
        }, 0)
      }
    },
    [refs, pagination, setPagination, filteredSafetyCases],
  )

  const _setActiveGeoJsonId = useCallback(
    (activeGeoJsonId: number | undefined) => {
      setActiveGeoJsonId(activeGeoJsonId)

      if (activeGeoJsonId) {
        scrollToSafetyCaseCard(activeGeoJsonId)
      }
    },
    [scrollToSafetyCaseCard],
  )

  const _setHazardActiveGeoJsonId = useCallback(
    (hazardActiveGeoJsonId: number | undefined) => {
      setHazardActiveGeoJsonId(hazardActiveGeoJsonId)

      const hazardSafetyCaseId = allHazards.find(hazard => hazard.id === hazardActiveGeoJsonId)?.safetyCaseId

      if (hazardSafetyCaseId) {
        scrollToSafetyCaseCard(hazardSafetyCaseId)
      }
    },
    [allHazards, scrollToSafetyCaseCard],
  )

  const activeSafetyCaseId = useMemo(
    () => activeGeoJsonId ?? allHazards.find(hazard => hazard.id === hazardActiveGeoJsonId)?.safetyCaseId,
    [activeGeoJsonId, hazardActiveGeoJsonId, allHazards],
  )

  const handleSafetyCaseLocationClick = useCallback((safetyCase: ISafetyCase) => {
    const hazardIds = (safetyCase.hazards || []).filter(hazard => hazard.geoJson).map(hazard => hazard.id)

    if (hazardIds.length > 0) {
      setHazardActiveGeoJsonId(hazardIds)
      setHazardInfoGeoJsonId(hazardIds.length === 1 ? hazardIds[0] : undefined)
    }
  }, [])

  const handleNavigateToSafetyCase = (safetyCase?: ISafetyCase) =>
    safetyCase?.id && navigate(`/safety-cases/${safetyCase?.id}`, { state: { backSearch: location.search } })

  const handleNavigateToSafetyCaseHazard = (hazard?: ISafetyCaseHazard) =>
    hazard?.safetyCaseId &&
    hazard?.id &&
    navigate(`/safety-cases/${hazard.safetyCaseId}/hazards/${hazard.id}`, { state: { backSearch: location.search } })

  const handleNavigateToSafetyCaseFromHazard = (hazard?: ISafetyCaseHazard) =>
    hazard?.safetyCaseId && navigate(`/safety-cases/${hazard.safetyCaseId}`, { state: { backSearch: location.search } })

  const handleOpenCreateSafetyCaseModal = useCallback(() => openCreateModal(), [openCreateModal])

  const handleCreateSafetyCase = useCallback(
    async (safetyCaseValues: ISafetyCaseValues) => {
      const safetyCaseAddUpsert = safetyCaseValuesToSafetyCaseAddUpsert(safetyCaseValues, {
        airportId,
      })

      if (!safetyCaseAddUpsert) {
        return
      }

      let createdSafetyCase
      try {
        createdSafetyCase = await createSafetyCase(safetyCaseAddUpsert)
      } catch (err) {
        appDispatch(showErrorAlert('Error creating safety case'))
        console.warn(err)
        return
      }

      appDispatch(showSuccessAlert('Safety case successfully created'))
      closeCreateModal()

      if (createdSafetyCase?.id) {
        navigate(`/safety-cases/${createdSafetyCase.id}`, { state: { backSearch: location.search } })
      }
    },
    [airportId, createSafetyCase, appDispatch, closeCreateModal, navigate, location.search],
  )

  const headerControls = useMemo(
    () => (hasSafetyCaseWriteAccess ? <Button onClick={handleOpenCreateSafetyCaseModal}>Create</Button> : undefined),
    [hasSafetyCaseWriteAccess, handleOpenCreateSafetyCaseModal],
  )

  return (
    <PageLayout title={pageTitle} headerControls={headerControls}>
      <CreateSafetyCaseModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        onSubmit={handleCreateSafetyCase}
        isSubmitting={isSafetyCaseCreating}
        hasWriteAccess={hasSafetyCaseWriteAccess}
      />

      <ContentMapLayout>
        {{
          content: (
            <div className="d-flex flex-column w-100">
              <SafetyCaseFilter searchFilter={searchFilter} setSearchFilter={setSearchFilter} />

              <SafetyCasesCardsList
                pagination={pagination}
                setPagination={setPagination}
                safetyCases={filteredSafetyCases}
                isSafetyCasesInitialLoading={isSafetyCasesInitialLoading}
                safetyCasesRefs={refs}
                activeSafetyCaseId={activeSafetyCaseId}
                isSafetyCasesLoading={isSafetyCasesLoading}
                handleNavigateToSafetyCase={handleNavigateToSafetyCase}
                onLocationClick={handleSafetyCaseLocationClick}
              />
            </div>
          ),
          map: (
            <Map>
              <MarkerLayout
                activeId={activeGeoJsonId}
                setActiveId={_setActiveGeoJsonId}
                infoId={infoGeoJsonId}
                setInfoId={setInfoGeoJsonId}
                geoJson={safetyCasesMarkersGeoJson}
                renderPopUp={(geoJson: IGeoJson) => {
                  const safetyCase = safetyCases?.find(safetyCase => safetyCase.id === geoJson.properties.id)

                  return <SafetyCasePopup safetyCase={safetyCase} onNavigateToSafetyCase={handleNavigateToSafetyCase} />
                }}
              />

              <PolygonLayout
                activeId={activeGeoJsonId}
                setActiveId={_setActiveGeoJsonId}
                infoId={infoGeoJsonId}
                setInfoId={setInfoGeoJsonId}
                geoJson={safetyCasesPolygonsGeoJson}
                renderPopUp={(geoJson: IGeoJson) => {
                  const safetyCase = safetyCases?.find(safetyCase => safetyCase.id === geoJson.properties.id)

                  return <SafetyCasePopup safetyCase={safetyCase} onNavigateToSafetyCase={handleNavigateToSafetyCase} />
                }}
              />

              <MarkerLayout
                activeId={hazardActiveGeoJsonId}
                setActiveId={_setHazardActiveGeoJsonId}
                infoId={hazardInfoGeoJsonId}
                setInfoId={setHazardInfoGeoJsonId}
                geoJson={hazardsMarkersGeoJson}
                renderPopUp={(geoJson: IGeoJson) => {
                  const hazard = allHazards.find(hazard => hazard.id === geoJson.properties.id)

                  return (
                    <SafetyCaseHazardPopup
                      safetyCaseHazard={hazard}
                      onNavigateToSafetyCaseHazard={handleNavigateToSafetyCaseHazard}
                      onNavigateToSafetyCase={handleNavigateToSafetyCaseFromHazard}
                    />
                  )
                }}
              />

              <PolygonLayout
                activeId={hazardActiveGeoJsonId}
                setActiveId={_setHazardActiveGeoJsonId}
                infoId={hazardInfoGeoJsonId}
                setInfoId={setHazardInfoGeoJsonId}
                geoJson={hazardsPolygonsGeoJson}
                renderPopUp={(geoJson: IGeoJson) => {
                  const hazard = allHazards.find(hazard => hazard.id === geoJson.properties.id)

                  return (
                    <SafetyCaseHazardPopup
                      safetyCaseHazard={hazard}
                      onNavigateToSafetyCaseHazard={handleNavigateToSafetyCaseHazard}
                      onNavigateToSafetyCase={handleNavigateToSafetyCaseFromHazard}
                    />
                  )
                }}
              />
            </Map>
          ),
        }}
      </ContentMapLayout>
    </PageLayout>
  )
}

export default SafetyCasesPage
